'use client';

import Image from "next/image";
import { FormEventHandler, useRef, useState } from "react";
import { AiInput, AiInputRef } from "@/components/AiInput";
import { createRAGQuerySDK, type TokenUsage } from "@letterhead/core";
import { Streamdown } from 'streamdown';


// Configuration - replace with your values
const RAG_CONFIG = {
    apiUrl: process.env.NEXT_PUBLIC_API_URL ?? 'https://api.letterhead.cloud',
    apiKey: process.env.NEXT_PUBLIC_LETTERHEAD_API ?? '',
};

type Message = {
    role: 'user' | 'assistant';
    content: string;
    error?: boolean;
};

export default function Try() {
    const [question, setQuestion] = useState<string>('');
    const [messages, setMessages] = useState<Message[]>([]);
    const [isStreaming, setIsStreaming] = useState<boolean>(false);
    const [metadata, setMetadata] = useState<{
        confidence?: number;
        sources?: number;
        tokens?: TokenUsage;
    }>({});

    const inputRef = useRef<AiInputRef>(null);
    const ragSDK = useRef(createRAGQuerySDK(RAG_CONFIG));

    const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault();
        if (!question.trim()) {
            return;
        }

        if (!RAG_CONFIG.apiKey) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'API key not configured. Please set NEXT_PUBLIC_API_KEY environment variable.',
                error: true
            }]);
            return;
        }

        const userQuestion = question.trim();
        setMessages(prev => [...prev, { role: 'user', content: userQuestion }]);
        setQuestion('');
        setIsStreaming(true);
        setMetadata({});

        try {
            let assistantMessage = '';
            const result = await ragSDK.current.query(
                {
                    question: userQuestion,
                    limit: 5,
                    minScore: 0.0,
                },
                {
                    onMetadata: (meta) => {
                        setMetadata({
                            confidence: meta.confidence,
                            sources: meta.sources_used,
                        });
                    },
                    onContent: (content, accumulated) => {
                        assistantMessage = accumulated;

                        setMessages(prev => {
                            const newMessages = [...prev];
                            const lastMessage = newMessages[newMessages.length - 1];
                            if (lastMessage && lastMessage.role === 'assistant' && !lastMessage.error) {
                                newMessages[newMessages.length - 1] = { role: 'assistant', content: accumulated };
                            } else {
                                newMessages.push({ role: 'assistant', content: accumulated });
                            }
                            return newMessages;
                        });
                    },
                    onDone: (fullResponse, tokenUsage) => {
                        if (tokenUsage) {
                            setMetadata(prev => ({
                                ...prev,
                                tokens: tokenUsage,
                            }));
                        }
                        setIsStreaming(false);
                    },
                    onError: (err) => {
                        setMessages(prev => [...prev, { role: 'assistant', content: err, error: true }]);
                        setIsStreaming(false);
                    },
                }
            );
        } catch (err: any) {
            setMessages(prev => [...prev, { role: 'assistant', content: err.message || 'An error occurred', error: true }]);
            setIsStreaming(false);
        }
    };

    const handleCancelStream = () => {
        ragSDK.current.cancel();
        setIsStreaming(false);
    };

    const handleClear = () => {
        setQuestion('');
        setMessages([]);
        setMetadata({});
    };

    return (
        <div>
            <section className="container mx-auto px-4">
                <div className="flex flex-col justify-center items-center py-20">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <Image src={'/ai.gif'} alt={"ai gif"} width={100} height={100} className="mx-auto" />
                        <p className="text-gray-600 dark:text-gray-400">
                            Ask Loci about Abuja Municipal Area Council (AMAC)
                        </p>
                    </div>

                    {/* Messages Section */}
                    {messages.length > 0 && (
                        <div className="w-full max-w-3xl mt-8 mb-8 space-y-4 text-[16px]">
                            {messages.map((message, index) => (
                                <div
                                    key={index}
                                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`max-w-[80%] rounded-lg p-2 ${message.role === 'user'
                                            ? ''
                                            : message.error
                                                ? 'bg-red-100 dark:bg-red-900'
                                                : ''
                                            }`}
                                    >
                                        {message.error ? (
                                            <div className="text-red-600 dark:text-red-400">
                                                <strong>Error:</strong> {message.content}
                                            </div>
                                        ) : message.role === 'user' ? (
                                            <div className="bg-[#212121] px-2 rounded-md py-2">
                                                {message.content}
                                            </div>
                                        ) : (
                                            <Streamdown key={index}>{message.content}</Streamdown>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Loading Animation */}
                            {isStreaming && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
                                <div className="flex justify-start">
                                    <div className="max-w-[80%] rounded-lg p-2">
                                        <div className="flex items-center space-x-2">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Clear Button */}
                            {!isStreaming && (
                                <div className="flex justify-center mt-4">
                                    <button
                                        onClick={handleClear}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Clear Chat
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Input Section */}
                    <div className="w-full flex justify-center">
                        <form onSubmit={handleSubmit}>
                            <AiInput
                                ref={inputRef}
                                onSubmit={() => handleSubmit}
                                className={'h-[130px] z-[90] w-full'}
                                placeholder='Ask loci'
                                disabled={isStreaming}
                                isStreaming={isStreaming}
                                onCancelStream={handleCancelStream}
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                            />
                        </form>
                    </div>
                </div>
            </section>
        </div>
    );
}
